import { Actor, SlidingState } from "../actor.js";
import { Layer, Tile } from "../tile.js";
import { actorDB } from "../const.js";
import { Playable } from "./playables.js";
import { Item } from "./items.js";
import { getTileWirable, WireOverlapMode, } from "../wires.js";
function findNextTeleport(teleport, rro, validateDestination) {
    const thisConstructor = Object.getPrototypeOf(teleport).constructor;
    let lastY = teleport.tile.y;
    let rolledOver = false;
    for (const tile of teleport.level.tiles(rro, teleport.tile.position)) {
        if (!rolledOver && Math.abs(tile.y - lastY) > 1)
            rolledOver = true;
        lastY = tile.y;
        const newTeleport = (tile[teleport.layer] instanceof thisConstructor
            ? tile[teleport.layer]
            : null);
        if (newTeleport &&
            (!validateDestination || validateDestination(newTeleport, rolledOver)))
            return newTeleport;
    }
    return teleport;
}
function findNextBlueTeleport(teleport, rro, validateDestination) {
    let lastY = teleport.tile.y;
    let rolledOver = false;
    for (const tile of teleport.level.tiles(rro, teleport.tile.position)) {
        if (!rolledOver && Math.abs(tile.y - lastY) > 1)
            rolledOver = true;
        lastY = tile.y;
        const res = validateDestination(tile, rolledOver);
        if (res)
            return res;
    }
    return null;
}
export class Teleport extends Actor {
    constructor() {
        super(...arguments);
        this.shouldProcessThing = false;
        this.requiresFullConnect = true;
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    actorCompletelyJoined() {
        this.shouldProcessThing = true;
    }
    actorOnTile(other) {
        var _a;
        if (other._internalIgnores(this))
            return;
        if (other.bonked)
            other.slidingState = SlidingState.NONE;
        if (!this.shouldProcessThing)
            return;
        this.shouldProcessThing = false;
        (_a = this.level.sfxManager) === null || _a === void 0 ? void 0 : _a.playOnce("teleport");
        this.onTeleport(other);
    }
}
Teleport.tags = ["machinery", "teleport"];
export function isBlueTeleportTarget(val) {
    return "isBlueTeleportTarget" in val && "takeTeleport" in val;
}
export function doBlueTeleport(teleport, other) {
    const thisNetwork = teleport.getTeleportOutputCircuit();
    const seenNetworks = new Set();
    const newTeleportish = findNextBlueTeleport(teleport, true, (tile, rolledOver) => {
        var _a, _b;
        while (other.newActor)
            other = other.newActor;
        const wirable = getTileWirable(tile);
        const tpNetworks = (_b = (_a = wirable.circuits) === null || _a === void 0 ? void 0 : _a.reduce((acc, val) => (val ? acc.concat(val) : acc), [])) !== null && _b !== void 0 ? _b : [];
        if (thisNetwork && tpNetworks.length === 0)
            return null;
        if (!thisNetwork && tpNetworks.length > 0 && !rolledOver) {
            for (const network of tpNetworks)
                seenNetworks.add(network);
            return null;
        }
        if (wirable instanceof Tile || !isBlueTeleportTarget(wirable))
            return null;
        const newTeleport = wirable;
        if (!thisNetwork && tpNetworks.some(val => seenNetworks.has(val)))
            return null;
        if (tpNetworks.length > 0 &&
            thisNetwork &&
            !newTeleport.getTeleportInputCircuit().includes(thisNetwork))
            return null;
        if (!thisNetwork &&
            tpNetworks.length > 0 &&
            !newTeleport.hasTag("janky-blue-teleport-overflow-target"))
            return null;
        if (newTeleport.isBusy(other))
            return null;
        return newTeleport;
    });
    const newTeleport = newTeleportish !== null && newTeleportish !== void 0 ? newTeleportish : teleport;
    teleport.giveUpTeleport(other);
    newTeleport.takeTeleport(other);
}
export class BlueTeleport extends Teleport {
    constructor() {
        super(...arguments);
        this.id = "teleportBlue";
        this.wireOverlapMode = WireOverlapMode.OVERLAP;
    }
    isBlueTeleportTarget() {
        return true;
    }
    takeTeleport(other) {
        other.oldTile = other.tile;
        other.tile = this.tile;
        other._internalUpdateTileStates(other.oldTile[other.layer] !== other);
        other.slidingState = SlidingState.STRONG;
    }
    giveUpTeleport(other) {
        other.tile.removeActors(other);
    }
    isBusy(other) {
        return (this.tile.hasLayer(Layer.MOVABLE) ||
            !other.checkCollisionFromTile(this.tile, other.direction, false, false));
    }
    actorJoined(other) {
        other.slidingState = SlidingState.STRONG;
    }
    onTeleport(other) {
        doBlueTeleport(this, other);
    }
    getTeleportOutputCircuit() {
        var _a;
        return (_a = this.circuits) === null || _a === void 0 ? void 0 : _a.find(val => val);
    }
    getTeleportInputCircuit() {
        var _a, _b;
        return ((_b = (_a = this.circuits) === null || _a === void 0 ? void 0 : _a.reduce((acc, val) => (val ? acc.concat(val) : acc), [])) !== null && _b !== void 0 ? _b : []);
    }
}
BlueTeleport.tags = ["machinery", "teleport", "janky-blue-teleport-overflow-target"];
actorDB["teleportBlue"] = BlueTeleport;
export class RedTeleport extends Teleport {
    constructor() {
        super(...arguments);
        this.id = "teleportRed";
        this.wireOverlapMode = WireOverlapMode.NONE;
    }
    actorJoined(other) {
        other.slidingState = SlidingState.WEAK;
    }
    onTeleport(other) {
        other.slidingState = SlidingState.WEAK;
        other.oldTile = other.tile;
        if (!this.wired || this.poweredWires)
            other.tile = findNextTeleport(this, false, (teleport) => {
                while (other.newActor)
                    other = other.newActor;
                if (teleport.tile.hasLayer(Layer.MOVABLE))
                    return false;
                if (teleport.wired && !teleport.poweredWires)
                    return false;
                for (let offset = 0; offset < 4; offset++) {
                    if (other.checkCollisionFromTile(teleport.tile, (other.direction + offset) % 4, false, false)) {
                        while (other.newActor)
                            other = other.newActor;
                        other.direction += offset;
                        other.direction %= 4;
                        return true;
                    }
                    while (other.newActor)
                        other = other.newActor;
                }
                return false;
            }).tile;
        while (other.newActor)
            other = other.newActor;
        other._internalUpdateTileStates();
        other.slidingState = SlidingState.WEAK;
        if (other instanceof Playable)
            other.hasOverride = true;
    }
    actorOnTile(other) {
        var _a;
        if (other._internalIgnores(this))
            return;
        if (other.bonked && other.slidingState) {
            if (this.wired && !this.poweredWires)
                other.slidingState = SlidingState.WEAK;
            else
                other.slidingState = SlidingState.NONE;
        }
        if (!this.shouldProcessThing)
            return;
        (_a = this.level.sfxManager) === null || _a === void 0 ? void 0 : _a.playOnce("teleport");
        this.shouldProcessThing = false;
        this.onTeleport(other);
    }
}
actorDB["teleportRed"] = RedTeleport;
export class GreenTeleport extends Teleport {
    constructor() {
        super(...arguments);
        this.id = "teleportGreen";
    }
    actorJoined(other) {
        other.slidingState = SlidingState.STRONG;
    }
    onTeleport(other) {
        other.slidingState = SlidingState.STRONG;
        // All green TPs
        const allTeleports = [];
        // TPs which do not have an actor on them
        const validTeleports = [];
        let targetTeleport;
        for (let teleport = findNextTeleport(this, false); teleport !== this; teleport = findNextTeleport(teleport, false)) {
            allTeleports.push(teleport);
            if (!teleport.tile.hasLayer(Layer.MOVABLE))
                validTeleports.push(teleport);
        }
        allTeleports.push(this);
        validTeleports.push(this);
        // We have only 1 teleport in level, do not even try anything
        if (allTeleports.length === 1) {
            targetTeleport = this;
        }
        else {
            // This is a wack CC2 bug, I guess, (Props to magical and eevee from CCBBCDS for figuring it out)
            const targetIndex = (this.level.random() % (allTeleports.length - 1)) %
                validTeleports.length;
            const dir = this.level.random() % 4;
            mainLoop: for (let i = 0; i < validTeleports.length; i++) {
                const index = i + targetIndex;
                const teleport = validTeleports[index];
                if (teleport === this)
                    continue;
                if (index >= validTeleports.length)
                    break;
                for (let offset = 0; offset < 4; offset++) {
                    if (other.checkCollisionFromTile(teleport.tile, (dir + offset) % 4, false, false)) {
                        while (other.newActor)
                            other = other.newActor;
                        other.direction = (dir + offset) % 4;
                        targetTeleport = teleport;
                        break mainLoop;
                    }
                    while (other.newActor)
                        other = other.newActor;
                }
            }
        }
        while (other.newActor)
            other = other.newActor;
        if (!targetTeleport)
            targetTeleport = this;
        other.oldTile = other.tile;
        other.tile = targetTeleport.tile;
        other._internalUpdateTileStates();
        other.slidingState = SlidingState.STRONG;
    }
}
actorDB["teleportGreen"] = GreenTeleport;
export class YellowTeleport extends Teleport {
    constructor() {
        super(...arguments);
        this.pickup = Item.prototype.pickup;
        this.id = "teleportYellow";
        this.destination = 2 /* ItemDestination.ITEM */;
        this.ignores = this.blocks;
        this.shouldPickup = true;
    }
    hasItemMod() {
        return Item.prototype.hasItemMod.apply(this);
    }
    actorJoined(other) {
        other.slidingState = SlidingState.WEAK;
    }
    blocks() {
        return false;
    }
    levelStarted() {
        // If this is the only yellow teleport at yellow start, never pick up
        this.shouldPickup = findNextTeleport(this, false) !== this;
    }
    onTeleport(other) {
        other.slidingState = SlidingState.WEAK;
        const newTP = findNextTeleport(this, true, teleport => {
            while (other.newActor)
                other = other.newActor;
            return (!teleport.tile.hasLayer(Layer.MOVABLE) &&
                other.checkCollisionFromTile(teleport.tile, other.direction, false, false));
        });
        while (other.newActor)
            other = other.newActor;
        let shouldTP = !(this.shouldPickup && newTP === this);
        if (!shouldTP) {
            other.slidingState = SlidingState.NONE;
            shouldTP = !this.pickup(other);
        }
        if (shouldTP) {
            if (other.tile !== newTP.tile) {
                other.oldTile = other.tile;
                other.tile = newTP.tile;
                other._internalUpdateTileStates();
            }
            other.slidingState = SlidingState.WEAK;
            if (other instanceof Playable)
                other.hasOverride = true;
        }
    }
}
YellowTeleport.tags = ["machinery", "teleport"];
actorDB["teleportYellow"] = YellowTeleport;
//# sourceMappingURL=teleport.js.map